import {
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { useState } from "react";
import { StatusDot } from "../../components/ui/patrones";
import { Etiqueta } from "../../components/ui/primitivos";
import { cn } from "../../lib/cn";
import type { OrdenListada } from "../../lib/consola-api";
import { COLUMNAS_KANBAN, etiquetaDeEstado, PUNTO, TONO } from "./estados";

/**
 * Kanban por estado, con arrastre (SPEC 0003, fase 3).
 *
 * **El arrastre es funcionalidad, no adorno**: mover una tarjeta de columna
 * CAMBIA el estado de la OT. Es como se usa el tablero en el taller, y por eso
 * entran los tres paquetes de `dnd-kit` — sin librería, la alternativa era un
 * menú en la tarjeta, que cambia cómo se trabaja y no solo cómo se ve.
 *
 * **La máquina de estados sigue mandando.** Soltar una tarjeta en una columna
 * ilegal no la mueve: se llama al mismo endpoint que los botones del detalle, y
 * el worker rechaza con 422 lo que no corresponde (ADR 0024). Acá no hay una
 * copia de las reglas — una copia se desincroniza y deja arrastrar cosas que el
 * servidor va a rechazar.
 */

const DISTANCIA_ARRASTRE = 8;

export function Kanban({
  ordenes,
  alMover,
  alAbrir,
}: {
  ordenes: readonly OrdenListada[];
  alMover: (orden: OrdenListada, estado: string) => Promise<void>;
  alAbrir: (o: OrdenListada) => void;
}) {
  const [arrastrando, setArrastrando] = useState<OrdenListada | null>(null);

  // Con `distance` el click sigue funcionando: sin esto, cualquier click sobre
  // la tarjeta arranca un arrastre y el botón de abrir deja de andar.
  const sensores = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: DISTANCIA_ARRASTRE } }),
  );

  function alSoltar(evento: DragEndEvent) {
    setArrastrando(null);
    const destino = evento.over?.id;
    const orden = ordenes.find((o) => o.id === evento.active.id);
    if (!orden || typeof destino !== "string" || orden.estado === destino) return;
    void alMover(orden, destino);
  }

  return (
    <DndContext
      sensors={sensores}
      onDragStart={(e: DragStartEvent) =>
        setArrastrando(ordenes.find((o) => o.id === e.active.id) ?? null)
      }
      onDragEnd={alSoltar}
      onDragCancel={() => setArrastrando(null)}
    >
      <div className="flex gap-4 overflow-x-auto pb-4">
        {COLUMNAS_KANBAN.map((estado) => (
          <Columna
            key={estado}
            estado={estado}
            ordenes={ordenes.filter((o) => o.estado === estado)}
            alAbrir={alAbrir}
          />
        ))}
      </div>

      {/* La tarjeta que viaja con el cursor. Sin esto se arrastra un hueco. */}
      <DragOverlay>{arrastrando ? <Tarjeta orden={arrastrando} arrastrada /> : null}</DragOverlay>
    </DndContext>
  );
}

function Columna({
  estado,
  ordenes,
  alAbrir,
}: {
  estado: string;
  ordenes: readonly OrdenListada[];
  alAbrir: (o: OrdenListada) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: estado });

  return (
    <div
      ref={setNodeRef}
      className={cn(
        "w-72 shrink-0 rounded-xl border p-3 transition-colors",
        // Resaltar la columna bajo el cursor: sin eso no se sabe dónde va a
        // caer la tarjeta hasta que se suelta.
        isOver ? "border-brand-300 bg-brand-50" : "border-neutral-200 bg-neutral-50",
      )}
    >
      <div className="mb-3 flex items-center gap-2">
        <StatusDot estado={PUNTO[estado] ?? "borrador"} />
        <span className="font-semibold text-neutral-700 text-xs uppercase tracking-wider">
          {etiquetaDeEstado(estado)}
        </span>
        <span className="ml-auto text-neutral-500 text-xs">{ordenes.length}</span>
      </div>

      <div className="space-y-2">
        {ordenes.map((o) => (
          <TarjetaArrastrable key={o.id} orden={o} alAbrir={alAbrir} />
        ))}
        {ordenes.length === 0 ? (
          <p className="py-6 text-center text-neutral-400 text-xs">Sin órdenes</p>
        ) : null}
      </div>
    </div>
  );
}

function TarjetaArrastrable({
  orden,
  alAbrir,
}: {
  orden: OrdenListada;
  alAbrir: (o: OrdenListada) => void;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: orden.id });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      // La original se atenúa mientras viaja la copia del overlay: da la
      // sensación de que la tarjeta se levantó, no de que se duplicó.
      className={cn("cursor-grab active:cursor-grabbing", isDragging && "opacity-40")}
    >
      <Tarjeta orden={orden} alAbrir={alAbrir} />
    </div>
  );
}

function Tarjeta({
  orden,
  arrastrada = false,
  alAbrir,
}: {
  orden: OrdenListada;
  arrastrada?: boolean;
  alAbrir?: (o: OrdenListada) => void;
}) {
  return (
    <div
      className={cn(
        "rounded-lg border border-neutral-200 bg-white p-3",
        arrastrada ? "rotate-2 shadow-lg" : "shadow-sm",
      )}
    >
      <div className="mb-1 flex items-start justify-between gap-2">
        <span className="font-mono text-sm">{orden.numero}</span>
        <Etiqueta tono={TONO[orden.estado] ?? "neutral"}>{orden.cantidad}</Etiqueta>
      </div>
      <p className="truncate font-medium text-sm">{orden.cliente}</p>
      <p className="truncate text-neutral-500 text-xs">{orden.producto}</p>
      <p className="mt-1 truncate text-neutral-400 text-xs">{orden.responsable ?? "sin asignar"}</p>
      {/* Un botón explícito y no doble-click sobre la tarjeta: el doble-click no
          se descubre, y con el arrastre encima compite por el mismo gesto. El
          umbral de 8px del sensor deja pasar el click sin iniciar arrastre. */}
      {alAbrir ? (
        <button
          type="button"
          onClick={() => alAbrir(orden)}
          className="mt-2 text-brand-700 text-xs hover:underline"
        >
          Abrir
        </button>
      ) : null}
    </div>
  );
}
